import React, { useEffect, useCallback, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { FaKey } from "react-icons/fa";
import { enqueueSnackbar } from "notistack";

import { fetchPrograms } from "../../../redux/program/programActions";
import { generateActivationKey } from "../../../services/StudentService";

import Table from "../../../components/Table/Table";
import Loader from "../../../components/Loader/Loader";
import Pagination from "../../../components/Pagination/Pagination";
import CardHeader from "../../../components/Card/CardHeader";

import StudentFilter from "../../Student/StudentList/StudentFilter";
import { tableConstants } from "./ActivationKeyTableConstant";
import { useStudents } from "../../Student/StudentList/hooks/useStudents";
import { handleApiError } from "../../../utils/errorHandler"; // adjust path

/* ================= COMPONENT ================= */

const StudentActivationKey = () => {
  const dispatch = useDispatch();
  const { programs } = useSelector((state) => state.program);

  const sessionId = localStorage.getItem("session_id");
  const programList = programs?.data || [];

  const {
    students,
    filters,
    setFilters,
    page,
    lastPage,
    isStudentLoading,
    isSearching,
    fetchStudents,
    searchStudents,
    resetStudents,
    handlePageChange,
  } = useStudents();

  const [rowLoader, setRowLoader] = useState(false);

  /* ================= INITIAL LOAD ================= */

  useEffect(() => {
    dispatch(fetchPrograms({ selected: 0 }));
    fetchStudents(1);
  }, [dispatch, fetchStudents]);

  /* ================= SEARCH ================= */

  const searchHandler = useCallback(() => {
    if (!filters.name && !filters.idNumber && !filters.programId) {
      enqueueSnackbar("Apply at least one filter", {
        variant: "info",
      });
      return;
    }
    searchStudents(1);
  }, [filters, searchStudents]);

  /* ================= RESET ================= */

  const resetHandler = useCallback(() => {
    resetStudents();
  }, [resetStudents]);

  /* ================= BULK GENERATE ================= */

  const generateKeyHandler = async () => {
    if (!filters.programId) {
      enqueueSnackbar("Please select class", { variant: "error" });
      return;
    }

    try {
      const studentsWithoutKey = students.filter((s) => !s.activation_key);

      if (!studentsWithoutKey.length) {
        enqueueSnackbar("All students already have activation keys", {
          variant: "info",
        });
        return;
      }

      const payload = {
        student_ids: studentsWithoutKey.map((s) => s.id),
        session_id: sessionId,
      };

      const res = await generateActivationKey(payload);

      if (res.status === 200) {
        enqueueSnackbar(
          res.data.message.success || "Keys generated successfully",
          { variant: "success" }
        );
        handlePageChange(1);
      }
    } catch (error) {
      handleApiError(error); // ✅ use centralized error handler
    }
  };

  /* ================= SINGLE STUDENT ================= */

  const generateSingleKey = async (row) => {
    if (row.suspended) {
      enqueueSnackbar("Student has been suspended", { variant: "error" });
      return;
    }

    try {
      setRowLoader(true);

      const payload = { student_ids: [row.id], session_id: sessionId };
      const res = await generateActivationKey(payload);

      if (res.status === 200) {
        enqueueSnackbar(
          res.data.message.success || "Activation key generated successfully",
          { variant: "success" }
        );
        handlePageChange(page);
      }
    } catch (error) {
      handleApiError(error); // ✅ pass the error here too
    } finally {
      setRowLoader(false);
    }
  };

  /* ================= RENDER ================= */

  return (
    <div className="card">
      <CardHeader title="Activation Key Generate" icon={<FaKey />} />

      <div className="card-body">
        <StudentFilter
          type="activation"
          programList={programList}
          filters={filters}
          setFilters={setFilters}
          onSearch={searchHandler}
          onReset={resetHandler}
          onGenerateKey={generateKeyHandler}
          isSearching={isSearching}
        />
        {isStudentLoading && <Loader />}
        <div className="evaluating-table mt-3">
          {students && students.length > 0 ? (
            <Table
              cols={tableConstants(
                generateSingleKey,
                rowLoader,
                filters.idNumber
              )}
              data={students}
              striped
              hoverable
            />
          ) : (
            <div className="text-center">No data found</div>
          )}
        </div>
      </div>

      <div className="card-footer">
        {!!students.length && (
          <Pagination
            totalPage={lastPage}
            handlePageChange={({ selected }) => handlePageChange(selected + 1)}
          />
        )}
      </div>
    </div>
  );
};

export default StudentActivationKey;
